import { useMemo, useState } from 'react'
import * as ProdutoHttp from '../../../../services/api/https/registration/produto.http'
import { getEmpresas } from '../../../../services/localstorage/localstorage.service'
import { errorHandler } from '../../../../services/util/error.service'
import { openReportInNewTab } from '../../../../services/util/report.service'

export const LOAD_MESSAGE = {
  NONE: 'NONE',
  GET_PRODUCTS: 'Adquirindo produtos',
  SEARCH_PRODUCTS: 'Buscando produtos',
}

export const BARCODE_TYPES = {
  PRODUCT_CODE: 'product_code',
  EAN: 'ean',
}

const NEW_PRODUCT = {
  id: null,
  product_code: '',
  company_id: '',
  name: '',
  weight: '',
  unit_sku: '',
  m3: '',
  actived: true,
  ordem_separacao: '',
  caixa_com_peso_variavel: false,
  grupo_produto_id: '',
  weight_liquido: '',
  qte_por_pallet: '',
  dias_para_vencimento: '',
  participa_calculo_paletizacao: true,
  picking_add_lote: false,
  picking_add_data_validade: false,
  picking_add_info_complementar: false,
  picking_add_data_fabricacao: false,
  picking_add_lote_recebimento: false,
  picking_add_data_validade_recebimento: false,
  inventario_add_lote: false,
  inventario_add_data_validade: false,
  un_agregadora: '',
  un_fracionada: '',
  estoque_minimo: '',
}

const INITIAL_STATE = {
  produtos: [],
  grupos: [],
  globalFilter: '',
  selectedEmpresa: '',
  empresas: [],
  codigoBarras: '',
  barcodeType: BARCODE_TYPES.PRODUCT_CODE,
  modalProduto: {
    show: false,
    data: { ...NEW_PRODUCT },
  },
  modalEan: {
    show: false,
    data: { id: null, product_code: 0, name: '' },
  },
  loadingMessage: LOAD_MESSAGE.NONE,
  errorMessage: null,
}

const MENU_ITEMS = ['Editar', 'Código de Barras']

export default function useProdutoHook() {
  const [state, setState] = useState({ ...INITIAL_STATE })

  const localDispatch = useMemo(() => {
    const toggleModalProduto = (show, data) => {
      setState(prevState => ({ ...prevState, modalProduto: { show, data } }))
    }

    const fetchProducts = (selectedEmpresa, codigoBarras = '') => {
      const apiCall =
        codigoBarras.trim() !== ''
          ? ProdutoHttp.getProductsByCompanyAndEan(selectedEmpresa.id, codigoBarras)
          : ProdutoHttp.getProdutos(selectedEmpresa.id)

      apiCall
        .then(items =>
          setState(prevState => ({
            ...prevState,
            produtos: items.produtos,
            grupos: items.grupos,
            loadingMessage: LOAD_MESSAGE.NONE,
          })),
        )
        .catch(err =>
          setState(prevState => ({
            ...prevState,
            errorMessage: errorHandler(err),
            loadingMessage: LOAD_MESSAGE.NONE,
          })),
        )
    }

    return {
      resetState() {
        setState(prevState => ({
          ...prevState,
          loadingMessage: LOAD_MESSAGE.NONE,
          errorMessage: null,
        }))
      },

      getProdutos(selectedEmpresa) {
        fetchProducts(selectedEmpresa)
      },

      getProductsByCompanyIdAndEan(selectedEmpresa, codigoBarras) {
        fetchProducts(selectedEmpresa, codigoBarras)
      },

      getProdutosExported(selectedEmpresa) {
        setState(prevState => ({ ...prevState, loadingMessage: LOAD_MESSAGE.GET_PRODUCTS }))
        ProdutoHttp.getProdutosWithEanOnExcel(selectedEmpresa.id)
          .then(res => {
            window.open(res)
            setState(prevState => ({ ...prevState, loadingMessage: LOAD_MESSAGE.NONE }))
          })
          .catch(err =>
            setState(prevState => ({
              ...prevState,
              errorMessage: errorHandler(err),
              loadingMessage: LOAD_MESSAGE.NONE,
            })),
          )
      },

      onBarcodeTypeChange(value) {
        setState(prevState => ({ ...prevState, barcodeType: value }))
      },

      async onGerarCodigosBarras() {
        const { selectedEmpresa, barcodeType } = state

        if (!selectedEmpresa) {
          setState(prevState => ({ ...prevState, errorMessage: 'Selecione a empresa desejada' }))
          return
        }

        const url = await ProdutoHttp.getProdutosCodigoBarrasReport(selectedEmpresa.id, barcodeType)
        openReportInNewTab(url)
      },

      getMenuItems() {
        return MENU_ITEMS
      },

      onInit() {
        setState(prevState => ({
          ...prevState,
          empresas: getEmpresas(),
          selectedEmpresa: getEmpresas()[0],
          loadingMessage: LOAD_MESSAGE.GET_PRODUCTS,
        }))
      },

      onThreeDotsClicked(menuItem, produto) {
        switch (menuItem) {
          case 'Editar':
            this.onMenuItemEditarClicked(produto)
            break
          case 'Código de Barras':
            this.onMenuItemCodigoBarraClicked(produto)
            break
          default:
            break
        }
      },

      onEmpresaChange(selectedEmpresa) {
        const { codigoBarras = '' } = state
        const hasFilter = codigoBarras.trim() !== ''

        setState(prevState => ({
          ...prevState,
          selectedEmpresa: selectedEmpresa,
          loadingMessage: hasFilter ? LOAD_MESSAGE.SEARCH_PRODUCTS : LOAD_MESSAGE.GET_PRODUCTS,
        }))

        if (hasFilter) {
          fetchProducts(selectedEmpresa, codigoBarras)
        }
      },

      onBtnNovo(selectedEmpresa) {
        if (selectedEmpresa === '')
          setState(prevState => ({ ...prevState, errorMessage: 'Selecione a empresa desejada' }))
        else toggleModalProduto(true, { ...NEW_PRODUCT, company_id: selectedEmpresa.id })
      },

      onModalProdutoClose() {
        toggleModalProduto(false, { ...NEW_PRODUCT })
      },

      onModalEanClose() {
        setState(prevState => ({ ...prevState, modalEan: { show: false, data: null } }))
      },

      onModalProdutoSave(produto) {
        setState(prevState => {
          const newProdutos = [...prevState.produtos]
          const index = newProdutos.findIndex(l => l.id === produto.id)
          if (index !== -1) newProdutos[index] = produto
          else newProdutos.push(produto)
          return { ...prevState, produtos: newProdutos, modalProduto: { show: false, data: null } }
        })
      },

      onMenuItemEditarClicked(produto) {
        const {
          id,
          product_code,
          company_id,
          name,
          weight,
          unit_sku,
          m3,
          actived,
          ordem_separacao,
          caixa_com_peso_variavel,
          grupo_produto_id,
          weight_liquido,
          qte_por_pallet,
          dias_para_vencimento,
          participa_calculo_paletizacao,
          picking_add_lote,
          picking_add_data_validade,
          picking_add_data_fabricacao,
          picking_add_info_complementar,
          picking_add_lote_recebimento,
          picking_add_data_validade_recebimento,
          inventario_add_lote,
          inventario_add_data_validade,
          un_agregadora,
          un_fracionada,
          estoque_minimo,
          updated_at,
        } = produto
        toggleModalProduto(true, {
          id,
          product_code,
          company_id,
          name,
          weight,
          unit_sku,
          m3,
          actived,
          ordem_separacao,
          caixa_com_peso_variavel,
          grupo_produto_id,
          weight_liquido,
          qte_por_pallet,
          dias_para_vencimento,
          participa_calculo_paletizacao,
          picking_add_lote,
          picking_add_data_validade,
          picking_add_data_fabricacao,
          picking_add_info_complementar,
          picking_add_lote_recebimento,
          picking_add_data_validade_recebimento,
          inventario_add_lote,
          inventario_add_data_validade,
          un_agregadora,
          un_fracionada,
          estoque_minimo,
          updated_at,
        })
      },

      onMenuItemCodigoBarraClicked(produto) {
        const { id, product_code, name } = produto
        setState(prevState => ({
          ...prevState,
          modalEan: { show: true, data: { id, product_code, name } },
        }))
      },

      onGlobalFilterChange(value) {
        setState(prevState => ({ ...prevState, globalFilter: value }))
      },

      onFilterChange(name, value) {
        setState(prevState => ({
          ...prevState,
          [name]: value,
        }))
      },

      onLimparFiltro() {
        setState(prevState => ({
          ...prevState,
          codigoBarras: '',
          loadingMessage: LOAD_MESSAGE.GET_PRODUCTS,
        }))
      },

      onBuscarFiltrado() {
        const { selectedEmpresa, codigoBarras = '' } = state

        if (!selectedEmpresa) {
          setState(p => ({ ...p, errorMessage: 'Selecione a empresa antes de buscar' }))
          return
        }

        if (codigoBarras.trim() === '') {
          setState(p => ({ ...p, errorMessage: 'Preencha o código de barras antes de buscar' }))
          return
        }

        setState(p => ({ ...p, loadingMessage: LOAD_MESSAGE.SEARCH_PRODUCTS }))
        fetchProducts(selectedEmpresa, codigoBarras)
      },
    }
  }, [state])

  return { state, localDispatch }
}
